import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowLeft, Receipt, Calendar, MapPin, CreditCard, CheckCircle, XCircle } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";
import client from "@/Graphql/apolloClient";
import { CREATE_CHECKOUT_SESSION, INVOICE, UPDATESTATUSMUTATION, ECOMMERCE_CONFIG } from "@/Graphql/Queries";
import { CREATE_PAYMENT_INTENT } from "@/Graphql/Mutations";
import { generateMockData } from "@/Graphql/MockData";
import { useToast } from "@/hooks/use-toast";
import CheckoutForm from "@/components/CheckoutForm";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from '@stripe/stripe-js';


interface PriceSummary {
  subTotal: number;
  netTotal: number;
  tax: {
    percent: number;
    name: string;
    value: number;
  };
  currency: { code: string; symbol: string };
}

interface CampaignInventory {
  inventoryName: string | undefined;
  billboardId: string;
  billboardName: string;
  billboardType: string;
  inventoryPrice: number;
  inventoryThumbnailUrl: string;
  mediaOwnerId: string;
  mediaOwnerName: string;
  inventoryType: string;
  inventoryResolutions: string;
  inventoryReports: {
    totalPotentialViews: number;
    totalUniqueReach: number;
    averageFrequency: number;
    cpm: number;
  };
}

interface InvoiceData {
  id: string;
  name: string;
  dealId: string;
  startDate: { date: string };
  endDate: { date: string };
  summaryReport: {
    potentialViews: string;
    uniqueReach: string;
    averageFrequency: number;
  };
  priceSummary: PriceSummary;
  campaignInventories: CampaignInventory[];
  campaignStatus?: string; // Add campaign status to interface
}

interface CheckoutProps {
  base_path: string;
  yourLogo: string;
  currencyCode: string;
}

const validatePaymentConfig = (accessKey: string, secretKey: string) => {
  if (!accessKey || !secretKey) {
    throw new Error("Payment configuration is incomplete");
  }
  return true;
};

const validateInvoiceData = (invoice: InvoiceData) => {
  if (!invoice.id || !invoice.dealId || !invoice.priceSummary?.netTotal) {
    throw new Error("Invalid invoice data");
  }
  return true;
};

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, '0');
  const month = date.toLocaleDateString('en-GB', { month: 'short' }).replace('Sept', 'Sep');
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
};

// stripePromise must be initialized inside the component to access accessKey state

export default function Checkout({ base_path, yourLogo, currencyCode }: CheckoutProps) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [campaignData, setCampaignData] = useState<InvoiceData | null>(null);
  const [campaignStatus, setCampaignStatus] = useState<string>("PAYMENT_PENDING");
  const [showPaymentConfirmation, setShowPaymentConfirmation] = useState(false);
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);
  const [showPaymentError, setShowPaymentError] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [isHandlingPaymentReturn, setIsHandlingPaymentReturn] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [accessKey, setAccessKey] = useState("");
  const [secretKey, setSecretKey] = useState("");
  const [billboardCurrencyCode, setBillboardCurrencyCode] = useState(currencyCode);
  const [userdata, setUserdata] = useState<any[]>([]);

  const [clientSecret, setClientSecret] = useState('');
  const hasPaymentIntentRequestedRef = useRef(false);
  const stripePromise = accessKey ? loadStripe(accessKey) : null;

  // Authentication details
  const token = localStorage.getItem("authToken");
  const userId = localStorage.getItem("userId");
  const userEmail = localStorage.getItem("userEmail");
  const userAccountData = localStorage.getItem("userAccountData");
  const companyId = localStorage.getItem("companyId") || import.meta.env.VITE_APP_COMPANY_ID || "";

  const currentDomain = window.location.origin;

  // Mutations
  const [createCheckoutSession] = useMutation(CREATE_CHECKOUT_SESSION);
  const [updateCampaignStatus] = useMutation(UPDATESTATUSMUTATION);

  // Fetch ecommerce config using GraphQL
  const { data: ecommerceConfigData, loading: configLoading, error: configError } = useQuery(ECOMMERCE_CONFIG, {
    variables: {
      accessToken: token,
      companyId: companyId
    },
    skip: !token || !companyId,
    fetchPolicy: "cache-and-network"
  });

  // Handle ecommerce config data from GraphQL
  useEffect(() => {
    if (ecommerceConfigData?.ecommerceConfig?.paymentIntegrationConfig) {
      const paymentConfig = ecommerceConfigData.ecommerceConfig.paymentIntegrationConfig;
      setAccessKey(paymentConfig.accessKey || "");
      // setSecretKey(paymentConfig.secretKey || ""); // Uncomment if secretKey is available in the response
    } else if (configError) {
      console.error("Error fetching ecommerce config:", configError);
      setErrorMessage("Error fetching payment configuration. Please try again later.");
    }
  }, [ecommerceConfigData, configError]);

  useEffect(() => {
    if (userAccountData) {
      const parsedUserAccountData = JSON.parse(userAccountData || "[]");
      console.log("print user account data-->", parsedUserAccountData);
      setUserdata(parsedUserAccountData);

    }
  }, [userAccountData]);

  // Handle return from Stripe payment (success or cancel)
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const sessionId = urlParams.get('session_id');
    const paymentIntent = urlParams.get('payment_intent');
    const redirectStatus = urlParams.get('redirect_status');
    const campaignId = urlParams.get('campaign');
    const canceled = urlParams.get('canceled');

    // Only proceed if we have either a session_id (Checkout Session) or payment_intent (Payment Intent) indicating return from Stripe
    if ((sessionId || paymentIntent) && token && campaignId) {
      console.log('Processing Stripe redirect:', {
        sessionId,
        paymentIntent,
        redirectStatus,
        campaignId,
        canceled
      });
      setIsHandlingPaymentReturn(true);

      // Check if payment was canceled
      if (canceled === 'true' || redirectStatus === 'failed') {
        console.log('Payment was canceled or failed, refetching invoice data');
        setErrorMessage(null);
        setShowPaymentError(false);
        setLoading(false); // Ensure loading state is reset
        // Clean up URL parameters for cancel case
        navigate(`${base_path}/checkout?campaign=${campaignId}`, { replace: true });
        setIsHandlingPaymentReturn(false);
        return;
      }

      // Check if payment was successful
      if (canceled !== 'true' && (redirectStatus === 'succeeded' || sessionId)) {
        console.log('Payment successful, fetching updated campaign data');
        
        // Reset client secret and payment intent state since payment is complete
        setClientSecret('');
        hasPaymentIntentRequestedRef.current = false;
        
        const fetchUpdatedCampaignData = async () => {
          try {
            let invoiceData = null;
            
            if (import.meta.env.VITE_APP_MOCK_DATA === "true") {
              const mockResponse: any = await generateMockData(INVOICE);
              const data = mockResponse.data;
              invoiceData = data.paymentInvoice;
              // For mock data, simulate approved status after successful payment
              invoiceData.campaignStatus = "APPROVED";
            } else {
              const { data } = await client.query({
                query: INVOICE,
                variables: {
                  accessToken: token,
                  campId: campaignId,
                },
                fetchPolicy: "network-only",
              });
              invoiceData = data.paymentInvoice;
            }

            if (invoiceData) {
              setCampaignData(invoiceData);
              setCampaignStatus(invoiceData.campaignStatus || "APPROVED");
              setBillboardCurrencyCode(invoiceData.priceSummary?.currency?.code || billboardCurrencyCode);
              
              // Show success modal
              setShowPaymentSuccess(true);
              setLoading(false);
              
              // Clear payment-related URL parameters after processing
              setTimeout(() => {
                navigate(`${base_path}/checkout?campaign=${campaignId}`, { replace: true });
              }, 100);
            } else {
              throw new Error("Failed to fetch updated campaign data.");
            }
          } catch (error: any) {
            console.error("Error fetching updated campaign data:", error);
            setErrorMessage(error.message || "Payment was successful but failed to fetch updated data. Please refresh the page.");

            toast({
              title: "Warning",
              description: "Payment was successful but failed to fetch updated data. Please refresh the page.",
              variant: "destructive",
            });
            setShowPaymentError(true);
            navigate(`${base_path}/checkout?campaign=${campaignId}`, { replace: true });
          } finally {
            setIsHandlingPaymentReturn(false);
          }
        };
        
        fetchUpdatedCampaignData();
      } else if (paymentIntent && redirectStatus && redirectStatus !== 'succeeded') {
        // Handle other redirect statuses (like requires_action, requires_payment_method, etc.)
        console.log('Payment requires additional action or failed:', redirectStatus);
        setErrorMessage(`Payment ${redirectStatus.replace('_', ' ')}. Please try again.`);
        setShowPaymentError(true);
        setClientSecret('');
        hasPaymentIntentRequestedRef.current = false;
        navigate(`${base_path}/checkout?campaign=${campaignId}`, { replace: true });
        setIsHandlingPaymentReturn(false);
      }
    }
  }, [location.search, token, navigate, base_path, billboardCurrencyCode]);

  // Fetch invoice details
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const campaignId = urlParams.get('campaign');
    const sessionId = urlParams.get('session_id');
    const paymentIntent = urlParams.get('payment_intent');
    const redirectStatus = urlParams.get('redirect_status');
    const success = urlParams.get('success');

    // Don't fetch invoice data if we're returning from a successful payment,
    // if we're currently handling a payment return, or if the success modal is showing
    if ((sessionId && success === 'true') || (paymentIntent && redirectStatus === 'succeeded') || isHandlingPaymentReturn || showPaymentSuccess) {
      console.log('Skipping invoice fetch - payment success flow will handle, payment return in progress, or success modal showing');
      setLoading(false); // Set loading to false since we're not fetching
      return;
    }

    const fetchInvoiceDetails = async () => {
      setLoading(true);
      try {
        const { data } = await client.query({
          query: INVOICE,
          variables: {
            accessToken: token,
            campId: campaignId,
          },
          fetchPolicy: "network-only",
        });

        const invoiceData = data.paymentInvoice;
        if (invoiceData) {
          setCampaignData(invoiceData);
          setCampaignStatus(invoiceData.campaignStatus || "PAYMENT_PENDING");
          setBillboardCurrencyCode(invoiceData.priceSummary?.currency?.code || currencyCode);
        } else {
          setErrorMessage("Campaign not found.");
        }
      } catch (error) {
        console.error("Error fetching invoice details:", error);
        setErrorMessage("Failed to load campaign details.");
      } finally {
        setLoading(false);
      }
    };

    if (campaignId) {
      if (import.meta.env.VITE_APP_MOCK_DATA === "true") {
        generateMockData(INVOICE)
          .then((mockResponse: any) => {
            const data = mockResponse.data;
            setCampaignData(data.paymentInvoice);
            setCampaignStatus(data.paymentInvoice?.campaignStatus || "PAYMENT_PENDING");
            setBillboardCurrencyCode(data.paymentInvoice?.priceSummary?.currency?.code || currencyCode);
            setLoading(false);
          })
          .catch((err: any) => {
            console.error("Error generating mock data:", err);
            setErrorMessage("Failed to load mock data.");
            setLoading(false);
          });
      } else {
        fetchInvoiceDetails();
      }
    }
  }, [currencyCode, location.search, isHandlingPaymentReturn, showPaymentSuccess]);

  const formatCurrency = (amount: number) => {
    return `${billboardCurrencyCode} ${new Intl.NumberFormat('en-AU', {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount)}`;
  };

  const calculateDuration = (startDate: string, endDate: string): string => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    return `${Math.ceil((end.getTime() - start.getTime()) / (1000 * 3600 * 24)) + 1} days`;
  };

  const handlePayNowClick = () => {
    setShowPaymentConfirmation(true);
    setShowPaymentError(false);
    setClientSecret(''); // Reset client secret
    hasPaymentIntentRequestedRef.current = false; // Reset ref to allow payment intent creation
  };

  const createPaymentIntent = async () => {
    try {
      const { data } = await client.mutate({
        mutation: CREATE_PAYMENT_INTENT,
        variables: {
          accessToken: token,
          dealId: campaignData?.dealId,
        },
      });

      if (!data || !data.createPaymentIntent) {
        throw new Error("Failed to create payment intent");
      }

      if (data && data.createPaymentIntent?.code !== 200) {
        throw new Error(data.createPaymentIntent?.message || "Payment intent creation failed");
      }

      const _clientSecret = data.createPaymentIntent.clientSecret;
      if (!_clientSecret) {
        throw new Error("No client secret received");
      }

      setClientSecret(_clientSecret);
    } catch (error) {
      console.error("Error creating payment intent:", error);
      setShowPaymentError(true);
      setErrorMessage(error instanceof Error ? error.message : "Failed to initialize payment");
      hasPaymentIntentRequestedRef.current = false;
      throw error;
    }
  };

  const handleConfirmPayment = async () => {
    setShowPaymentConfirmation(false);

    // Prevent multiple calls, especially in React Strict Mode
    if (hasPaymentIntentRequestedRef.current) return;
    hasPaymentIntentRequestedRef.current = true;

    try {
      await createPaymentIntent();
    } catch (error) {
      console.error("Error creating payment intent:", error);
      setShowPaymentError(true);
      setErrorMessage("Failed to initialize payment. Please try again.");
      hasPaymentIntentRequestedRef.current = false;
    }
  };

  const handlePaymentSuccess = () => {
    setShowPaymentSuccess(false);
    toast({
      title: "Success",
      description: "Payment completed successfully!",
      variant: "default",
    });
    navigate(`${base_path}/payment-invoice?campaign=${campaignData?.id}`);
  };

  const handlePaymentSuccessClose = () => {
    setShowPaymentSuccess(false);
    // Just close the modal, don't navigate - user stays on checkout page with approved status
  };

  if (loading || configLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="text-center">
            <div className="animate-spin w-12 h-12 border-4 border-emerald-600 border-t-transparent rounded-full mx-auto mb-4"></div>
            <h1 className="text-2xl font-bold text-gray-900">Loading...</h1>
          </div>
        </div>
      </div>
    );
  }

  // Show processing screen when handling payment return but don't have campaign data yet
  if (isHandlingPaymentReturn && !campaignData) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="text-center">
            <div className="animate-spin w-12 h-12 border-4 border-emerald-600 border-t-transparent rounded-full mx-auto mb-4"></div>
            <h1 className="text-2xl font-bold text-gray-900">Processing Payment...</h1>
            <p className="text-gray-600 mt-2">Please wait while we confirm your payment.</p>
          </div>
        </div>
      </div>
    );
  }

  // Show minimal layout when payment success modal is active - but still show the checkout page
  // The success modal will appear on top

  if (!campaignData || errorMessage) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900">Checkout Not Found</h1>
            <p className="text-gray-600 mt-2">{errorMessage || "The requested campaign checkout could not be found."}</p>
            <Button
              variant="outline"
              onClick={() => navigate(`${base_path}/my-campaigns`)}
              className="mt-4"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Campaigns
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const digitalBillboards = campaignData.campaignInventories?.filter(
    (inventory) => inventory.inventoryType === "digital"
  ) || [];
  const classicBillboards = campaignData.campaignInventories?.filter(
    (inventory) => inventory.inventoryType === "classic"
  ) || [];

  const paymentIntentOptions = {
    clientSecret: clientSecret,
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-[95vw] mx-auto px-4 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate(`${base_path}/my-campaigns`)}
          className="mr-4 mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center">
            <div>
              <h1 className="text-3xl font-bold text-emerald-600">Checkout</h1>
              <p className="text-gray-600 mt-1">
                {campaignStatus === "APPROVED"
                  ? `Payment completed for ${campaignData.name}`
                  : `Complete your payment for Pending Payment Campaign`}
              </p>
            </div>
          </div>
          {campaignStatus === "APPROVED" ? (
            <Badge variant="secondary" className="bg-green-100 text-green-800">
              Payment Approved
            </Badge>
          ) : (
            <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
              Pending Payment
            </Badge>
          )}
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  Campaign Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Campaign Name</p>
                    <p className="font-semibold">{campaignData.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Campaign ID</p>
                    <p className="font-semibold">{campaignData.dealId}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Start Date</p>
                    <p className="font-semibold flex items-center">
                      <Calendar className="h-4 w-4 mr-1 text-emerald-600" />
                      {formatDate(campaignData.startDate.date)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">End Date</p>
                    <p className="font-semibold flex items-center">
                      <Calendar className="h-4 w-4 mr-1 text-emerald-600" />
                      {formatDate(campaignData.endDate.date)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Duration</p>
                    <p className="font-semibold">{calculateDuration(campaignData.startDate.date, campaignData.endDate.date)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <MapPin className="h-5 w-5 mr-2 text-emerald-600" />
                  Inventory Details
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4 max-h-[500px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                  {campaignData.campaignInventories?.map((inventory) => (
                    <div key={inventory.billboardId} className="flex items-center gap-4 p-4 border rounded-lg">
                      <img
                        src={inventory.inventoryThumbnailUrl || `${import.meta.env.VITE_APP_FOLDER_PATH}/nomedia.jpg`}
                        alt={inventory.inventoryName}
                        className="w-20 h-16 object-cover rounded"
                      />
                      <div className="flex-1">
                        <h3 className="font-semibold">{inventory.inventoryName}</h3>
                        <Badge
                          variant="secondary"
                          className={`${inventory.inventoryType === 'digital' || inventory.inventoryType  === 'Digital' ? 'bg-blue-100 text-blue-700' :
                            inventory.inventoryType  === 'classic' || inventory.inventoryType  === 'Classic' ? 'bg-green-100 text-green-700' :
                              inventory.inventoryType  === 'Package' ? 'bg-purple-100 text-purple-700' :
                                inventory.inventoryType  === 'Transit' ? 'bg-orange-100 text-orange-700' :
                                  'bg-gray-100 text-gray-700'
                            }`}
                        >
                          {inventory.inventoryType.charAt(0).toUpperCase() + inventory.inventoryType.slice(1).toLowerCase()}
                        </Badge>
                        <div>
                          <p className="text-xs text-emerald-600 hover:text-emerald-700 font-medium mb-2 mt-1">By {(inventory.inventoryType === "Package" || inventory.inventoryType?.toLowerCase() === "package") 
                            ? "Multiple Media Owners" : inventory.mediaOwnerName}</p>
                           
                        </div>
                        <p className="text-sm text-gray-500">{inventory.inventoryReports?.totalPotentialViews?.toLocaleString() || 'N/A'} impressions</p>
                        <p className="text-sm text-gray-500">Resolution: {inventory.inventoryResolutions}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-lg">{formatCurrency(inventory.inventoryPrice)}</p>
                        <p className="text-sm text-gray-500">for {calculateDuration(campaignData.startDate.date, campaignData.endDate.date)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
            {/* Billing Details */}
            <Card>
              <CardHeader>
                <CardTitle>Billing Details</CardTitle>
              </CardHeader>
              <CardContent>

                <div className="space-y-2">
                  {userdata[0]?.userCompanyName && (
                    <p className="text-gray-600">
                      Company Name: {userdata[0].userCompanyName}
                    </p>
                  )}
                  {userdata[0]?.email && (
                    <p className="text-gray-600">
                      Email: {userdata[0].email}
                    </p>
                  )}
                  {userdata[0]?.userCompanyAddress && (
                    <p className="text-gray-600">
                      Address: {userdata[0].userCompanyAddress}
                    </p>
                  )}
                  {userdata[0]?.userCity && (
                    <p className="text-gray-600">
                      City: {userdata[0].userCity}
                    </p>
                  )}
                  {userdata[0]?.userCompanyState && (
                    <p className="text-gray-600">
                      State: {userdata[0].userCompanyState}
                    </p>
                  )}
                  {userdata[0]?.userCompanyPostcode && (
                    <p className="text-gray-600">
                      Postcode: {userdata[0].userCompanyPostcode}
                    </p>
                  )}
                  {userdata[0]?.userCountry && (
                    <p className="text-gray-600">
                      Country: {userdata[0].userCountry}
                    </p>
                  )}
                </div>

              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  Payment Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between">
                  <span>Sub-total</span>
                  <span>{formatCurrency(campaignData.priceSummary.subTotal)}</span>
                </div>
                <div className="flex justify-between">
                  <span>{campaignData.priceSummary.tax.name} ({Math.round(campaignData.priceSummary.tax.percent)}%)</span>
                  <span>{formatCurrency(campaignData.priceSummary.tax.value)}</span>
                </div>
                <Separator />
                <div className="flex justify-between text-lg font-semibold">
                  <span>Total Amount</span>
                  <span className="text-emerald-600">{formatCurrency(campaignData.priceSummary.netTotal)}</span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Payment Method</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {!clientSecret && (
                  <div className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">Credit Card</span>
                      <div className="flex gap-2">
                        <div className="w-8 h-5 bg-blue-600 rounded text-white text-xs flex items-center justify-center">VISA</div>
                      <div className="w-8 h-5 bg-red-600 rounded text-white text-xs flex items-center justify-center">MC</div>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600">
                    {campaignStatus === "APPROVED"
                      ? "Payment completed successfully"
                      : "Secure payment powered by Stripe"}
                  </p>
                </div>
              )}

                {/* Show loading state when payment intent is being created */}
                {campaignStatus !== "APPROVED" && hasPaymentIntentRequestedRef.current && !clientSecret && (
                  <div className="border rounded-lg p-4">
                    <div className="text-center">
                      <div className="animate-spin w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full mx-auto mb-2"></div>
                      <p className="text-sm text-gray-600">Preparing payment...</p>
                    </div>
                  </div>
                )}

                {/* Stripe Checkout Form */}
                {campaignStatus !== "APPROVED" && clientSecret && stripePromise && (
                  <div className="border rounded-lg p-4">
                    <Elements stripe={stripePromise} options={paymentIntentOptions}>
                      <CheckoutForm 
                        totalAmount={formatCurrency(campaignData.priceSummary.netTotal)}
                        onPaymentError={(error) => {
                          setErrorMessage(error);
                          setShowPaymentError(true);
                        }}
                      />
                    </Elements>
                  </div>
                )}

                {/* Pay Now Button */}
                {campaignStatus !== "APPROVED" && !clientSecret && !hasPaymentIntentRequestedRef.current && (
                  <Button
                    className="w-full btn-emerald-gradient"
                    size="lg"
                    onClick={handlePayNowClick}
                    disabled={isProcessingPayment}
                  >
                    <CreditCard className="h-4 w-4 mr-2" />
                    Proceed Payment - {formatCurrency(campaignData.priceSummary.netTotal)}
                  </Button>
                )}
                
                {campaignStatus === "APPROVED" && (
                  <Button
                    variant="outline"
                    className="w-full"
                    size="lg"
                    onClick={() => navigate(`${base_path}/payment-invoice?campaign=${campaignData?.id}`)}
                  >
                    <Receipt className="h-4 w-4 mr-2" />
                    View Invoice
                  </Button>
                )}
                <div className="text-center">
                  <p className="text-xs text-gray-500">
                    {campaignStatus === "APPROVED"
                      ? "Payment has been processed successfully"
                      : "Your payment information is secure and encrypted"}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <Dialog open={showPaymentConfirmation} onOpenChange={setShowPaymentConfirmation}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Payment confirmation</DialogTitle>
          </DialogHeader>
          <div className="text-center py-6">
            <Receipt className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <p className="text-lg mb-2">Are you sure you</p>
            <p className="text-lg mb-6">want to proceed with the payment?</p>
            <div className="flex gap-3 justify-center">
              <Button
                variant="outline"
                onClick={() => {
                  setShowPaymentConfirmation(false);
                  setClientSecret(''); // Reset client secret
                  hasPaymentIntentRequestedRef.current = false; // Reset ref
                }}
                className="px-8"
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmPayment}
                className="bg-emerald-600 hover:bg-emerald-700 px-8"
              >
                Pay
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showPaymentSuccess} onOpenChange={handlePaymentSuccessClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Payment Successful</DialogTitle>
          </DialogHeader>
          <div className="text-center py-6">
            <div className="w-16 h-16 mx-auto mb-4 bg-emerald-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-emerald-600" />
            </div>
            <p className="text-lg mb-2">Payment successful!</p>
            <p className="text-gray-600 mb-6">Your payment has been processed successfully.</p>
            <div className="flex gap-3 justify-center">
              {/* <Button
                variant="outline"
                onClick={handlePaymentSuccessClose}
                className="px-8"
              >
                Close
              </Button> */}
              <Button
                onClick={handlePaymentSuccess}
                className="bg-emerald-600 hover:bg-emerald-700 px-8"
              >
                View Invoice
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showPaymentError} onOpenChange={setShowPaymentError}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Payment Failed</DialogTitle>
          </DialogHeader>
          <div className="text-center py-6">
            <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <p className="text-lg mb-2">Payment failed</p>
            <p className="text-gray-600 mb-6">{errorMessage || "There was an error processing your payment. Please try again."}</p>
            <div className="flex gap-3 justify-center">
              <Button
                variant="outline"
                onClick={() => {
                  setShowPaymentError(false);
                  setClientSecret(''); // Reset client secret
                  hasPaymentIntentRequestedRef.current = false; // Reset ref
                }}
                className="px-8"
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  setShowPaymentError(false);
                  handlePayNowClick(); // Restart the payment process
                }}
                className="bg-emerald-600 hover:bg-emerald-700 px-8"
              >
                Try Again
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
}